import { useEffect } from "react";

import { filter, isEmpty, orderBy } from "lodash/fp";
import { DateTime } from "luxon";

import { ReportSortUtils } from "@app/analytics/MeetingReports/ReportsTable/utils";
import type { Report } from "@app/analytics/MeetingReports/types";
import useUserMetadata from "@app/common/auth/useUserMetadata";
import type { DateRange } from "@app/common/types";

import useFetchMeetingsInBackground from "./useFetchMeetingsInBackground";
import useUnseenStore from "../stores/unseen";
import { getReportDate, isUnseen } from "../utils";

const sortReportsDesc = (reports?: Report[]) => {
  if (isEmpty(reports)) return [];
  return orderBy(ReportSortUtils.date, "desc", reports);
};

const getUnseenCount = (
  data: Report[],
  storedLastSeenValue: string | null
): number => {
  if (!storedLastSeenValue || isEmpty(data)) return 0;
  const mostRecentStartTimeFromData = getReportDate(data[0]);
  const storedValue = DateTime.fromISO(storedLastSeenValue);

  if (
    mostRecentStartTimeFromData &&
    storedValue <= mostRecentStartTimeFromData
  ) {
    const unseenMeetings = filter(
      (meeting) => isUnseen(meeting, storedLastSeenValue),
      data
    );
    return unseenMeetings.length;
  }
  return 0;
};

const useUnseenReports = ({ dateRanges }: { dateRanges: DateRange[] }) => {
  // fetch "incomplete" and "shared with me" reports in the background,
  // so we can display the unseen counts in the tabs
  const {
    incompleteMeetingsQuery,
    sharedWithMeMeetingsQuery,
    sharedWithMeTopicsQuery,
  } = useFetchMeetingsInBackground({ dateRanges });

  const {
    unseenIncompleteCount,
    unseenSharedWithMeCount,
    setUnseenIncompleteCount,
    setUnseenSharedWithMeCount,
  } = useUnseenStore();

  const { userMetadata, updateUserMetadata } = useUserMetadata();

  const incompleteReports = sortReportsDesc(incompleteMeetingsQuery.meetings);

  const unsortedSharedReports = [
    ...sharedWithMeMeetingsQuery.meetings,
    ...sharedWithMeTopicsQuery.topics,
  ];

  const sharedReports = sortReportsDesc(unsortedSharedReports);

  useEffect(() => {
    if (incompleteMeetingsQuery.isLoading) return;

    const unseen = getUnseenCount(
      incompleteReports,
      userMetadata?.lastSeenIncompleteDate ?? null
    );
    if (unseen !== unseenIncompleteCount) {
      setUnseenIncompleteCount(unseen);
    }
  }, [incompleteReports.length, incompleteMeetingsQuery.isLoading]);

  const isSharedWithMeLoading =
    sharedWithMeMeetingsQuery.isLoading || sharedWithMeTopicsQuery.isLoading;

  useEffect(() => {
    if (isSharedWithMeLoading) return;

    const unseen = getUnseenCount(
      sharedReports,
      userMetadata?.lastSeenSharedWithMeDate ?? null
    );
    if (unseen !== unseenSharedWithMeCount) {
      setUnseenSharedWithMeCount(unseen);
    }
  }, [isSharedWithMeLoading, sharedReports.length]);

  const resetIncompleteCount = () => {
    setUnseenIncompleteCount(0);
  };

  const resetSharedWithMeCount = () => {
    setUnseenSharedWithMeCount(0);
  };

  const resetLastSeenSharedWithMe = () => {
    const mostRecentReport = sharedReports[0];
    const lastSeenDate = (
      mostRecentReport ? getReportDate(mostRecentReport) : DateTime.now()
    ).toISO();
    updateUserMetadata.mutate({ lastSeenSharedWithMeDate: lastSeenDate });
    resetSharedWithMeCount();
  };

  const resetLastSeenIncomplete = () => {
    const mostRecentReport = incompleteReports[0];
    const lastSeenDate = (
      mostRecentReport ? getReportDate(mostRecentReport) : DateTime.now()
    ).toISO();
    updateUserMetadata.mutate({ lastSeenIncompleteDate: lastSeenDate });
    resetIncompleteCount();
  };

  return {
    lastSeenIncompleteDate: userMetadata?.lastSeenIncompleteDate,
    lastSeenSharedWithMeDate: userMetadata?.lastSeenSharedWithMeDate,
    unseenIncompleteCount,
    unseenSharedWithMeCount,
    resetLastSeenSharedWithMe,
    resetLastSeenIncomplete,
  };
};

export default useUnseenReports;
